'use client';

import React from 'react';
import Link from 'next/link';

import { Phone, Users, UserCircle, FlaskConical, BarChart2 } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export type MacroSection = 'chiamate' | 'pazienti' | 'operatori' | 'lab' | 'analytics';

interface MacroItem {
  id: MacroSection;
  label: string;
  icon: React.ElementType;
  href: string;
  matchPrefixes: string[];
}

export const MACRO_SECTIONS: MacroItem[] = [
  {
    id: 'chiamate',
    label: 'Chiamate',
    icon: Phone,
    href: '/call-dashboard',
    matchPrefixes: [
      '/call-dashboard', '/call-detail', '/call-management', '/call-history',
      '/voicemail', '/call-routing', '/call-recordings', '/call-whisper',
      '/browser-calling', '/twilio-flex', '/callbacks', '/follow-ups',
      '/followup-automation', '/urgent', '/call-whisper',
    ],
  },
  {
    id: 'pazienti',
    label: 'Pazienti',
    icon: Users,
    href: '/patient-management',
    matchPrefixes: [
      '/patient-management', '/patients', '/patient-threads', '/patient-portal',
      '/intake', '/kiosk', '/samples', '/results', '/consents',
      '/home-collection', '/exam-catalog', '/exam-prep', '/exams',
      '/prescriptions', '/referrals', '/blacklist',
    ],
  },
  {
    id: 'operatori',
    label: 'Operatori',
    icon: UserCircle,
    href: '/operator-dashboard',
    matchPrefixes: [
      '/operator-dashboard', '/operator-presence', '/operator-profile',
      '/operator-settings', '/operators', '/supervisor', '/wallboard',
      '/operator-leaderboard', '/operator-comparison', '/operator-sla',
      '/daily-goals', '/shifts', '/shift-swap', '/worked-hours',
      '/availability-grid', '/weekly-availability', '/session-replay',
      '/handover-history', '/operator-chat',
    ],
  },
  {
    id: 'lab',
    label: 'Lab',
    icon: FlaskConical,
    href: '/lab/profile',
    matchPrefixes: [
      '/lab', '/settings', '/scripts', '/studio-manager', '/announcements',
      '/supervisor-broadcast', '/phone-lookup', '/numbers-management',
      '/twilio-health', '/alert-notifications', '/voice-intelligence',
      '/branches', '/physicians', '/billing-advanced', '/agreements',
      '/quality', '/audit-trail', '/feature-flags', '/admin',
      '/sms', '/whatsapp-notifications', '/conversations',
      '/unified-conversations', '/video-consultation', '/campaigns',
      '/twilio-notify', '/security',
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: BarChart2,
    href: '/analytics',
    matchPrefixes: [
      '/analytics', '/ai-insights', '/reports', '/export', '/invoices',
      '/call-dashboard/metrics',
    ],
  },
];

export function getActiveMacroSection(pathname: string): MacroSection | null {
  for (const section of MACRO_SECTIONS) {
    for (const prefix of section.matchPrefixes) {
      if (pathname === prefix || pathname.startsWith(prefix + '/')) {
        return section.id;
      }
    }
  }
  return null;
}

interface TopBarProps {
  activeSection: MacroSection | null;
}

export default function TopBar({ activeSection }: TopBarProps) {
  return (
    <nav
      className="flex items-center h-11 px-4 bg-primary shrink-0 gap-1"
      aria-label="Navigazione principale"
    >
      {MACRO_SECTIONS.map(section => {
        const Icon = section.icon;
        const isActive = activeSection === section.id;
        return (
          <Link
            key={section.id}
            href={section.href}
            aria-current={isActive ? 'page' : undefined}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-[13px] font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${
              isActive
                ? 'bg-white/20 text-white' :'text-white/70 hover:text-white hover:bg-white/10'
            }`}
          >
            <Icon size={14} aria-hidden="true" />
            <span>{section.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
